import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ADD_AUTH } from '../app-config.token';
import { SpinnerService } from './loading.service';

type RefreshResponse = {
  success?: boolean;
  data?: {
    token?: string;
  };
};

@Injectable({ providedIn: 'root' })
export class SessionKeepaliveService {
  private readonly TOKEN_KEY = 'identity.token';
  private readonly USER_KEY = 'identity.user';
  private readonly WARNING_WINDOW_MS = 5 * 60 * 1000;
  private readonly CHECK_EVERY_MS = 30 * 1000;

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly spinnerService = inject(SpinnerService);

  private passiveWatcherStarted = false;
  private checkTimerId: ReturnType<typeof setInterval> | null = null;
  private dialogTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private promptInProgress = false;
  private pendingDecision$?: ReplaySubject<boolean | UrlTree>;

  startPassiveWatcher(): void {
    if (this.passiveWatcherStarted) return;
    this.passiveWatcherStarted = true;

    this.checkTokenHealth();
    this.checkTimerId = setInterval(() => {
      this.checkTokenHealth();
    }, this.CHECK_EVERY_MS);
  }

  handleExpiredSession(): Observable<boolean | UrlTree> {
    if (this.pendingDecision$) {
      return this.pendingDecision$.asObservable();
    }

    const decision$ = new ReplaySubject<boolean | UrlTree>(1);
    this.pendingDecision$ = decision$;

    this.openRenewDialog(
      () => {
        this.refreshToken().subscribe((ok) => {
          if (ok) {
            decision$.next(true);
          } else {
            decision$.next(this.buildLoginTree());
          }
          decision$.complete();
          this.cleanupPrompt();
        });
      },
      () => {
        this.forceLogout();
        decision$.next(this.buildLoginTree());
        decision$.complete();
        this.cleanupPrompt();
      }
    );

    return decision$.asObservable();
  }

  forceLogout(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.USER_KEY);
    } catch {
      // ignore
    }

    if (this.router.url !== '/login') {
      this.router.navigate(['/login']);
    }
  }

  private checkTokenHealth(): void {
    const token = this.getToken();
    if (!token) {
      this.closePrompt();
      return;
    }

    const expMs = this.getTokenExpiryMs(token);
    if (!expMs) {
      this.forceLogout();
      return;
    }

    const remainingMs = expMs - Date.now();
    if (remainingMs <= this.WARNING_WINDOW_MS && !this.promptInProgress) {
      this.openRenewDialog(
        () => {
          this.refreshToken().subscribe((ok) => {
            if (!ok) {
              this.forceLogout();
            }
            this.cleanupPrompt();
          });
        },
        () => {
          this.forceLogout();
          this.cleanupPrompt();
        }
      );
    }
  }

  private refreshToken(): Observable<boolean> {
    return this.http.post<RefreshResponse>('auth/refresh', {}, {
      context: new HttpContext().set(ADD_AUTH, true),
    }).pipe(
      map((resp) => {
        const token = String(resp?.data?.token || '').trim();
        if (!resp?.success || !token) {
          return false;
        }

        try {
          sessionStorage.setItem(this.TOKEN_KEY, token);
        } catch {
          return false;
        }

        return true;
      }),
      catchError(() => of(false))
    );
  }

  private openRenewDialog(onAccept: () => void, onRejectOrTimeout: () => void): void {
    if (this.promptInProgress) return;

    this.promptInProgress = true;
    this.spinnerService.suppress(true);
    this.clearDialogTimeout();

    this.dialogTimeoutId = setTimeout(() => {
      this.closePrompt();
      onRejectOrTimeout();
    }, this.WARNING_WINDOW_MS);

    this.confirmationService.confirm({
      key: 'session-renew-dialog',
      header: 'Sesión por expirar',
      message: 'Tu sesión está por expirar. ¿Deseas mantenerte conectado?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, mantener sesión',
      rejectLabel: 'No, cerrar sesión',
      acceptButtonStyleClass: 'session-renew-accept',
      rejectButtonStyleClass:
        'session-renew-reject',
      closable: false,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => {
        this.closePrompt();
        onAccept();
      },
      reject: () => {
        this.closePrompt();
        onRejectOrTimeout();
      },
    });
  }

  private closePrompt(): void {
    this.confirmationService.close();
    this.clearDialogTimeout();
    this.spinnerService.suppress(false);
    this.promptInProgress = false;
  }

  private cleanupPrompt(): void {
    this.pendingDecision$ = undefined;
    this.spinnerService.suppress(false);
    this.promptInProgress = false;
    this.clearDialogTimeout();
  }

  private clearDialogTimeout(): void {
    if (this.dialogTimeoutId) {
      clearTimeout(this.dialogTimeoutId);
      this.dialogTimeoutId = null;
    }
  }

  private buildLoginTree(): UrlTree {
    return this.router.createUrlTree(['/login']);
  }

  private getToken(): string | null {
    try {
      return sessionStorage.getItem(this.TOKEN_KEY);
    } catch {
      return null;
    }
  }

  private getTokenExpiryMs(token: string): number {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return 0;

      const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payloadBase64.padEnd(payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      const expSeconds = Number(payload?.exp);

      if (!expSeconds) return 0;
      return expSeconds * 1000;
    } catch {
      return 0;
    }
  }
}
